import React, { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface SimulatedPauseEvent {
  job_id: string;
  job_name: string;
  started_at: string;
  resumed_at: string;
  duration_minutes: number;
  price_at_pause: number;
  peak_price_mwh: number;
  estimated_mwh_saved: number;
  estimated_savings: number;
}

export interface DryRunResult {
  id: number;
  customer_id: number;
  month: string;
  cluster_mw: number;
  threshold: number;
  region: string;
  simulated_events: SimulatedPauseEvent[];
  gross_savings: number;
  eloplus_fee: number;
  net_savings: number;
  hours_paused: number;
  uptime_impact_pct: number;
  is_synthetic_data: boolean;
  created_at: string;
}

interface DryRunPanelProps {
  result: DryRunResult;
  defaultCompanyName?: string;
}

const cardStyle: React.CSSProperties = {
  background: "#13213A",
  border: "1px solid #1F355B",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
};

const statCardStyle: React.CSSProperties = {
  ...cardStyle,
  padding: "14px 16px",
  display: "grid",
  gap: 6,
};

const TEAL: [number, number, number] = [14, 165, 160];
const ORANGE: [number, number, number] = [194, 101, 15];

function money(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({
  label,
  value,
  sublabel,
  accent,
}: {
  label: string;
  value: string;
  sublabel?: string;
  accent?: string;
}) {
  return (
    <div style={statCardStyle}>
      <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: accent || "#F8FAFC" }}>{value}</div>
      {sublabel && <div style={{ fontSize: 11, color: "#64748B" }}>{sublabel}</div>}
    </div>
  );
}

function DryRunBanner() {
  return (
    <div
      style={{
        background: "#3F2A0B",
        border: "2px solid #C2650F",
        borderRadius: 10,
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 18 }}>⚠️</span>
      <div style={{ color: "#FDBA74", fontWeight: 800, fontSize: 14 }}>
        DRY RUN — No jobs were paused. This is a simulation only.
      </div>
    </div>
  );
}

export default function DryRunPanel({ result, defaultCompanyName }: DryRunPanelProps) {
  const [preparedFor, setPreparedFor] = useState(defaultCompanyName || "");
  const [exporting, setExporting] = useState(false);

  const chartData = useMemo(
    () =>
      [...result.simulated_events]
        .sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime())
        .map((e) => ({
          label: formatTimestamp(e.started_at),
          savings: e.estimated_savings,
          job: e.job_name,
        })),
    [result.simulated_events]
  );

  const exportPdf = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const margin = 40;
      const pageW = doc.internal.pageSize.getWidth();

      const headerH = 88;
      doc.setFillColor(...TEAL);
      doc.rect(0, 0, pageW, headerH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.text("EloPlus.AI", margin, 52);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Confidential — Prepared for ${preparedFor || "[Company]"}`, margin, 72);

      let y = headerH + 26;
      doc.setFillColor(...ORANGE);
      doc.rect(margin, y, pageW - margin * 2, 26, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("DRY RUN — No jobs were paused. This is a simulation only.", pageW / 2, y + 17, {
        align: "center",
      });
      y += 46;

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("Dry Run Report", margin, y);
      y += 26;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(`Month: ${result.month}  ·  Region: ${result.region || "—"}`, margin, y);
      y += 16;
      doc.text(
        `Cluster capacity: ${result.cluster_mw.toFixed(0)} MW · Threshold: $${result.threshold.toFixed(0)}/MWh`,
        margin,
        y
      );
      y += 16;
      if (result.is_synthetic_data) {
        doc.setTextColor(180, 83, 9);
        doc.text("Note: based on simulated price data (no real ISO history available).", margin, y);
        y += 16;
      }

      autoTable(doc, {
        startY: y + 10,
        margin: { left: margin, right: margin },
        head: [["Metric", "Value"]],
        body: [
          ["Gross Savings (simulated)", money(result.gross_savings)],
          ["EloPlus Fee (17.5%)", money(result.eloplus_fee)],
          ["Net Savings (simulated)", money(result.net_savings)],
          ["Hours Paused (simulated)", result.hours_paused.toFixed(1)],
          ["Uptime Impact", `${result.uptime_impact_pct.toFixed(2)}%`],
          ["Pause Events Simulated", String(result.simulated_events.length)],
        ],
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [15, 94, 93] },
      });

      let nextY =
        (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 200;
      nextY += 26;

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Simulated Pause Events (would-have-happened, not executed)", margin, nextY);

      autoTable(doc, {
        startY: nextY + 10,
        margin: { left: margin, right: margin },
        head: [["Started", "Job", "Price/MWh", "MWh Saved", "Savings", "Duration (m)"]],
        body: result.simulated_events.map((evt) => [
          new Date(evt.started_at).toLocaleString(),
          evt.job_name,
          `$${evt.price_at_pause.toFixed(2)}`,
          evt.estimated_mwh_saved.toFixed(3),
          money(evt.estimated_savings),
          evt.duration_minutes.toFixed(0),
        ]),
        styles: { fontSize: 8, cellPadding: 5 },
        headStyles: { fillColor: [30, 64, 175] },
      });

      let footerY =
        (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? nextY + 200;
      footerY += 24;
      doc.setFont("helvetica", "oblique");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(
        "This report is a retrospective simulation against historical grid prices. No pause command",
        margin,
        footerY
      );
      doc.text("was ever issued, no agent webhook was called, and no cluster was contacted.", margin, footerY + 10);

      doc.save(`EloPlus_DryRun_${result.month}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <DryRunBanner />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard label="Gross Savings" value={money(result.gross_savings)} accent="#2DD4BF" />
        <StatCard
          label="Net Savings"
          value={money(result.net_savings)}
          sublabel={`after ${money(result.eloplus_fee)} fee (17.5%)`}
          accent="#6EE7B7"
        />
        <StatCard label="Hours Paused" value={result.hours_paused.toFixed(1)} />
        <StatCard label="Uptime Impact" value={`${result.uptime_impact_pct.toFixed(2)}%`} />
      </div>

      {result.is_synthetic_data && (
        <div
          style={{
            background: "#3F2A0B",
            border: "1px solid #92400E",
            borderRadius: 10,
            padding: "10px 14px",
            color: "#FCD34D",
            fontSize: 13,
          }}
        >
          This simulation is based on simulated price data — no real ISO/EIA history was available
          for {result.region || "this region"} over this period. Treat these figures as illustrative.
        </div>
      )}

      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 14px", fontSize: 18, color: "#F8FAFC" }}>
          Simulated Savings by Pause Event
        </h2>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#243B63" strokeDasharray="4 4" />
              <XAxis dataKey="label" stroke="#94A3B8" minTickGap={30} />
              <YAxis stroke="#94A3B8" />
              <Tooltip
                formatter={(value) => [`$${Number(value).toFixed(2)}`, "Simulated Savings"]}
                labelFormatter={(_, payload) =>
                  payload && payload[0] ? String(payload[0].payload.job) : ""
                }
                contentStyle={{
                  background: "#0F1E35",
                  border: "1px solid #1F355B",
                  borderRadius: 10,
                  color: "#E2E8F0",
                }}
              />
              <Bar dataKey="savings" fill="#0EA5A0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {result.simulated_events.length === 0 && (
          <div style={{ color: "#94A3B8", fontSize: 13, marginTop: 8 }}>
            No spike intervals above threshold were found for this period.
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 14px", fontSize: 18, color: "#F8FAFC" }}>
          Simulated Pause Events ({result.simulated_events.length})
        </h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#94A3B8", borderBottom: "1px solid #1F355B" }}>
                <th style={{ padding: "8px 10px" }}>Job</th>
                <th style={{ padding: "8px 10px" }}>Started</th>
                <th style={{ padding: "8px 10px" }}>Resumed</th>
                <th style={{ padding: "8px 10px" }}>Duration</th>
                <th style={{ padding: "8px 10px" }}>Price at Pause</th>
                <th style={{ padding: "8px 10px" }}>MWh Saved</th>
                <th style={{ padding: "8px 10px" }}>Savings</th>
              </tr>
            </thead>
            <tbody>
              {result.simulated_events.map((evt, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #1F355B" }}>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>{evt.job_name}</td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>
                    {new Date(evt.started_at).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>
                    {new Date(evt.resumed_at).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>
                    {evt.duration_minutes.toFixed(0)} min
                  </td>
                  <td style={{ padding: "8px 10px", color: "#FCA5A5", fontWeight: 700 }}>
                    ${evt.price_at_pause.toFixed(2)}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>
                    {evt.estimated_mwh_saved.toFixed(3)}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#6EE7B7", fontWeight: 700 }}>
                    {money(evt.estimated_savings)}
                  </td>
                </tr>
              ))}
              {result.simulated_events.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: "12px 10px", color: "#94A3B8" }}>
                    No simulated pause events for this scenario.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: 4, flex: "1 1 260px" }}>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>Prepared for (shown on the PDF cover)</span>
          <input
            type="text"
            value={preparedFor}
            onChange={(e) => setPreparedFor(e.target.value)}
            placeholder="[Company]"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #334155",
              background: "#0B1220",
              color: "#F8FAFC",
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => void exportPdf()}
          disabled={exporting}
          style={{
            background: "#1D4ED8",
            color: "#EFF6FF",
            border: "1px solid #3B82F6",
            borderRadius: 10,
            padding: "10px 18px",
            fontWeight: 800,
            cursor: exporting ? "wait" : "pointer",
            opacity: exporting ? 0.75 : 1,
          }}
        >
          {exporting ? "Generating…" : "Export PDF"}
        </button>
      </div>
    </div>
  );
}
